import React, { memo, useRef, useEffect, useState } from 'react';
import axios from 'axios';
import { X, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';
import logoSmall from '../NorthAI_Logo_Small_Zoomed.png';
import { sanitize } from '../utils/sanitize';
import TalkingPoints, { parseTalkingPoints } from './TalkingPoints';
import API_BASE_URL from '../apiConfig';
import SURFACES from '../utils/surfaces';
import { formatKm, formatOpportunity } from '../utils/formatters';

/**
 * The briefing card's surfaces come from utils/surfaces, which every page and
 * panel now shares. The card previously kept its own copy of these tokens, plus
 * an entire second light scheme behind `panelMode`; a few names below are local
 * aliases over the shared set so the rest of this file reads unchanged.
 */
const S = {
  ...SURFACES,
  shell:  SURFACES.page,
  card:   `${SURFACES.card} ${SURFACES.border}`,
  rule:   SURFACES.border,
  pitch:  'text-emerald-300',
  action: 'text-indigo-300',
  chip:   'bg-gray-900/40 hover:bg-gray-900/60',
};


const asSentence = (clauses) => {
  if (!clauses.length) return null;
  const joined = clauses.join('; ');
  return `${joined.charAt(0).toUpperCase()}${joined.slice(1)}.`;
};

/**
 * Split `geokg_insight` into the pitch and the market context.
 *
 * It is templated rather than free prose — clauses are joined with "; " and the
 * product clause always reads "customers like this nearby buy X — lead with
 * that" (backend/geokg/scoring/geokg_scorer.py, `_build_geokg_insight`). So it
 * separates reliably into two different things that previously shared one box:
 * what to sell (an instruction, belongs up top) and why this lead ranks here
 * (a reason, belongs with the rationale).
 *
 * Anything that does not match the template falls through as context, so an
 * insight we don't recognise is still shown in full rather than dropped.
 */
export const splitInsight = (insight) => {
  const text = sanitize(insight || '').trim();
  if (!text) return { pitch: null, context: null };

  const clauses = text.replace(/\.\s*$/, '').split(/;\s*/).map((c) => c.trim()).filter(Boolean);
  const idx = clauses.findIndex((c) => /lead with that/i.test(c));
  if (idx === -1) return { pitch: null, context: asSentence(clauses) };

  const product = clauses[idx].match(/\bbuy\s+(.+?)\s*[—–-]\s*lead with that/i);
  const pitch = product
    ? `${product[1].trim()} — similar customers nearby buy it.`
    : `${clauses[idx].charAt(0).toUpperCase()}${clauses[idx].slice(1)}.`;

  return { pitch, context: asSentence(clauses.filter((_, i) => i !== idx)) };
};

/**
 * Score colour. The old ramp painted >= 90 red and >= 70 orange — the best lead
 * of the day rendered in the colour that means stop. High now reads green, and
 * red stays available for genuine warnings.
 */
const scoreTone = (score) => (
  score >= 80
    ? { bar: 'bg-emerald-500', text: 'text-emerald-300' }
    : score >= 60
      ? { bar: 'bg-blue-500', text: 'text-blue-300' }
      : { bar: 'bg-gray-500', text: 'text-gray-300' }
);

/**
 * One collapsible row. Shared by the evidence and talking-point disclosures so
 * the two cannot drift into different shapes, paddings or type sizes.
 */
const Disclosure = ({ label, hint, open, onClick, testId }) => {
  const Chevron = open ? ChevronUp : ChevronDown;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={open}
      data-testid={testId}
      className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg border ${S.rule} ${S.chip} transition-colors`}
    >
      <span className={`flex items-center gap-2 text-xs font-semibold ${S.action}`}>
        {label}
        {hint != null && <span className={`font-normal ${S.faint}`}>{hint}</span>}
      </span>
      <Chevron className={`w-4 h-4 ${S.muted}`} />
    </button>
  );
};

/**
 * "What drove this score" — a lazy, on-demand expander showing the evidence the
 * AI evaluated (from the reasoning graph). It fetches only when opened, and
 * caches, so it never touches briefing generation or the initial render.
 */
const ReasoningEvidence = ({ globalId, score }) => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState('idle'); // idle | loading | done | empty | error
  const [evidence, setEvidence] = useState([]);

  const handleToggle = async (e) => {
    e.stopPropagation();
    if (open) { setOpen(false); return; }
    setOpen(true);
    if (status === 'done' || status === 'empty') return; // already fetched — use cache
    setStatus('loading');
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/graph/entities/${encodeURIComponent(globalId)}/decisions`
      );
      const decisions = res.data?.decisions || [];
      const ev = decisions.length ? (decisions[0].evidence || []) : [];
      setEvidence(ev);
      setStatus(ev.length ? 'done' : 'empty');
    } catch (err) {
      setStatus('error');
    }
  };

  // Split the scorecard into what helped, what hurt, and what was weighed but
  // didn't move the score. Each group is sorted by magnitude (biggest first).
  const positives = evidence.filter((e) => Number(e.points) > 0).sort((a, b) => b.points - a.points);
  const negatives = evidence.filter((e) => Number(e.points) < 0).sort((a, b) => a.points - b.points);
  const neutrals = evidence.filter((e) => Number(e.points) === 0);
  // Shared bar scale so a +40 bar reads twice as long as a +20 bar across groups.
  const maxAbs = Math.max(1, ...evidence.map((e) => Math.abs(Number(e.points) || 0)));

  const Row = ({ ev, tone }) => {
    const pts = Number(ev.points) || 0;
    const pct = Math.max(6, Math.round((Math.abs(pts) / maxAbs) * 100));
    const barColor = tone === 'pos' ? 'bg-emerald-400/80' : 'bg-amber-400/80';
    const ptsColor = tone === 'pos' ? 'text-emerald-300' : 'text-amber-300';
    return (
      <li className="space-y-1">
        <div className="flex items-baseline justify-between gap-3">
          <span className={`font-medium ${S.body}`}>{ev.label || ev.signal_type}</span>
          <span className={`flex-shrink-0 font-mono font-semibold tabular-nums ${ptsColor}`}>
            {pts > 0 ? `+${ev.points}` : ev.points}
          </span>
        </div>
        <div className="h-1.5 rounded-full overflow-hidden bg-gray-700/60">
          <div className={`h-full rounded-full ${barColor}`} style={{ width: `${pct}%` }} />
        </div>
        {ev.reasoning && (
          <span className={`block leading-snug ${S.muted}`}>{sanitize(ev.reasoning)}</span>
        )}
      </li>
    );
  };

  const GroupHeader = ({ icon, label, tone }) => (
    <div className={`flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide ${
      tone === 'pos' ? 'text-emerald-300' : 'text-amber-300'
    }`}>
      <span aria-hidden="true">{icon}</span> {label}
    </div>
  );

  return (
    <div>
      <Disclosure
        label="What drove this score"
        hint={Number.isFinite(Number(score)) ? Math.round(score) : null}
        open={open}
        onClick={handleToggle}
        testId="why-this-toggle"
      />
      {open && (
        <div className={`mt-2 px-3 py-3 text-xs rounded-lg border ${S.rule} bg-gray-900/20`}>
          {status === 'loading' && <span className={S.muted}>Loading reasoning…</span>}
          {status === 'error' && <span className={S.muted}>Reasoning unavailable right now.</span>}
          {status === 'empty' && (
            <span className={S.muted}>No detailed reasoning recorded for this recommendation.</span>
          )}
          {status === 'done' && (
            <div className="space-y-3">
              {positives.length > 0 && (
                <div className="space-y-2">
                  <GroupHeader icon="▲" label="Helped" tone="pos" />
                  <ul className="space-y-2">
                    {positives.map((ev, i) => <Row key={`p${i}`} ev={ev} tone="pos" />)}
                  </ul>
                </div>
              )}
              {negatives.length > 0 && (
                <div className="space-y-2">
                  <GroupHeader icon="▼" label="Held it back" tone="neg" />
                  <ul className="space-y-2">
                    {negatives.map((ev, i) => <Row key={`n${i}`} ev={ev} tone="neg" />)}
                  </ul>
                </div>
              )}
              {neutrals.length > 0 && (
                <div className={`pt-1 leading-snug ${S.faint}`}>
                  Also weighed: {neutrals.map((e) => e.label || e.signal_type).join(', ')}.
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Talking points, collapsed by default. They are for the doorstep, not for
 * triage — showing all of them for five leads at once was most of the card's
 * height. The count is on the toggle so it is obvious they exist.
 */
const TalkingPointsSection = ({ text }) => {
  const [open, setOpen] = useState(false);
  const count = parseTalkingPoints(text).length;
  if (!count) return null;

  return (
    <div>
      <Disclosure
        label="Talking points"
        hint={count}
        open={open}
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        testId="talking-points-toggle"
      />
      {open && (
        <div className="mt-3">
          <TalkingPoints text={text} accent="blue" panelMode={true} />
        </div>
      )}
    </div>
  );
};

const AgentBriefing = ({
  briefingData,
  onClose,
  onAccept,
  onAcceptAll,
  onReject,
  isLeadInVisitList,
  loadingLeadId,
  isAcceptingAll,
  panelMode = false,
  mobileSheet = false,
  onLeadFocus,
  focusedLeadId,
}) => {
  const sheetRef = useRef(null);
  // Refs for each briefing card, keyed by lead id, used to scroll the
  // focused card into view when focus is driven from the map.
  const cardRefs = useRef({});

  useEffect(() => {
    if (focusedLeadId == null) return;
    const node = cardRefs.current[focusedLeadId];
    if (node && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [focusedLeadId]);

  // Swipe-down to close when rendered as mobile bottom sheet
  useEffect(() => {
    if (!mobileSheet) return;
    const el = sheetRef.current;
    if (!el) return;
    let startY = null;
    const onStart = (e) => { startY = e.touches[0].clientY; };
    const onEnd = (e) => {
      if (startY !== null && e.changedTouches[0].clientY - startY > 80) onClose();
      startY = null;
    };
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchend', onEnd);
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchend', onEnd);
    };
  }, [mobileSheet, onClose]);

  if (!briefingData || briefingData.length === 0) {
    return null;
  }

  const inner = (
    <div
      data-testid="agent-briefing"
      className={panelMode
        ? `flex-1 min-h-0 w-full overflow-hidden flex flex-col ${S.shell}`
        : `${S.shell} rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col`
      }
    >
      {/* Header — blue/purple brand gradient aligned with NavRail + LeadDetailPanel */}
      <div className="relative bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 px-5 py-4 text-white overflow-hidden flex-shrink-0">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent" />
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="relative flex-shrink-0">
              <img src={logoSmall} alt="NorthAI" className="w-9 h-9 object-cover rounded-full ring-2 ring-white/30" />
              <Sparkles className="w-3.5 h-3.5 text-yellow-300 absolute -top-1 -right-1 drop-shadow" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-bold tracking-tight truncate">Daily AI Briefing</h2>
              <p className="text-indigo-100 text-xs mt-0.5 font-medium">
                {briefingData.length} {briefingData.length === 1 ? 'recommendation' : 'recommendations'}
              </p>
            </div>
          </div>
          <button aria-label="Close briefing"
            onClick={onClose}
            title="Close briefing"
            data-testid="briefing-close"
            className="flex-shrink-0 text-white/80 hover:text-white hover:bg-white/15 p-2 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className={`flex-1 overflow-y-auto scroll-pane px-4 py-4 space-y-4 ${S.shell}`}>
        {briefingData.map((lead, idx) => {
          const leadGlobalId = lead.global_id || `lead_${lead.id}`;
          const isFocused = focusedLeadId === lead.id;
          const isClickable = typeof onLeadFocus === 'function';
          const inList = isLeadInVisitList(leadGlobalId);
          const busy = loadingLeadId !== null || isAcceptingAll;

          const score = Number(lead.score) || 0;
          const tone = scoreTone(score);
          const distance = formatKm(lead.distance_km);
          const { pitch, context } = splitInsight(lead.geokg_insight);

          // The rationale/summary is the model's narrative; the map context is
          // an extra sentence of the same kind, so they read as one paragraph.
          const narrative = sanitize(lead.summary || lead.rationale || '');

          // Context line: facts that colour the decision but never drive it.
          const facts = [
            lead.forecasted_sales != null && `¥${Number(lead.forecasted_sales).toLocaleString()} forecast`,
            formatOpportunity(lead.opportunity_score) && `${formatOpportunity(lead.opportunity_score)} opportunity`,
            lead.priority && `${String(lead.priority).toLowerCase()} priority`,
            lead.status && String(lead.status).replace(/_/g, ' '),
          ].filter(Boolean);

          return (
            <div
              key={leadGlobalId}
              data-testid="briefing-card"
              data-lead-id={lead.id}
              data-lead-global-id={leadGlobalId}
              ref={(el) => {
                if (el) cardRefs.current[lead.id] = el;
                else delete cardRefs.current[lead.id];
              }}
              onClick={isClickable ? () => onLeadFocus(lead) : undefined}
              className={`rounded-xl p-4 border relative transition-all ${S.card} ${
                isClickable ? 'cursor-pointer' : ''
              } ${isFocused ? 'ring-2 ring-purple-500 ring-offset-2 ring-offset-gray-900' : ''} ${
                loadingLeadId === lead.id ? 'opacity-50' : ''
              }`}
            >
              {/* Numbered badge — ties card to matching numbered marker on the map */}
              <div
                className={`absolute -top-2.5 -left-2.5 w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-lg bg-gradient-to-br from-blue-500 to-purple-600 ${
                  isFocused ? 'ring-2 ring-purple-300' : ''
                }`}
                aria-hidden="true"
              >
                {idx + 1}
              </div>
              {loadingLeadId === lead.id && (
                <div className={`absolute inset-0 rounded-xl flex items-center justify-center z-10 bg-gray-900/70`}>
                  <div className="animate-spin rounded-full h-10 w-10 border-4 border-purple-500 border-t-transparent" />
                </div>
              )}

              {/* ── Identity: who, where, how good ── */}
              <h3 className={`text-base font-bold leading-snug ${S.title}`}>{lead.name}</h3>
              {(distance || lead.address) && (
                <p className={`text-xs mt-0.5 truncate ${S.muted}`}>
                  {distance && <span className="font-semibold text-emerald-300">{distance} away</span>}
                  {distance && lead.address && ' · '}
                  {lead.address && <span>{sanitize(lead.address)}</span>}
                </p>
              )}
              <div className="flex items-center gap-2 mt-2">
                <span className={`text-xs ${S.muted}`}>Smart Score</span>
                <span className={`text-lg font-bold leading-none tabular-nums ${tone.text}`}>
                  {Math.round(score)}
                </span>
                <span className="flex-1 max-w-[96px] h-1.5 rounded-full overflow-hidden bg-gray-700">
                  <span
                    className={`block h-full rounded-full ${tone.bar}`}
                    style={{ width: `${Math.max(0, Math.min(100, score))}%` }}
                  />
                </span>
              </div>

              {/* ── Lead with: the pitch, promoted out of the map-insight box ── */}
              {pitch && (
                <div className={`mt-3 pt-3 border-t ${S.rule}`}>
                  <p className={S.label}>Lead with</p>
                  <p className={`text-sm font-semibold leading-snug mt-1 ${S.pitch}`}>{pitch}</p>
                </div>
              )}

              {/* ── Why it's ranked here: narrative + context + the drill-down ── */}
              {(narrative || context || facts.length > 0) && (
                <div className={`mt-3 pt-3 border-t ${S.rule} space-y-2`}>
                  <p className={S.label}>Why it's ranked here</p>
                  {(narrative || context) && (
                    <p className={`text-sm leading-relaxed ${S.body}`}>
                      {[context, narrative].filter(Boolean).join(' ')}
                    </p>
                  )}
                  {facts.length > 0 && (
                    <p className={`text-xs tabular-nums ${S.muted}`}>{facts.join(' · ')}</p>
                  )}
                  <ReasoningEvidence globalId={leadGlobalId} score={score} />
                </div>
              )}

              {/* ── Talking points, collapsed ── */}
              {lead.talking_points && (
                <div className={`mt-3 pt-3 border-t ${S.rule}`}>
                  <TalkingPointsSection text={lead.talking_points} />
                </div>
              )}

              {/* ── Actions. Asymmetric: adding is the common path, and a
                     mis-tapped reject suppresses the lead for up to a year. ── */}
              <div className="flex items-center gap-2 mt-4">
                <button
                  onClick={(e) => { e.stopPropagation(); onAccept(lead.id); }}
                  disabled={inList || busy}
                  data-testid="briefing-accept"
                  data-lead-id={lead.id}
                  data-in-list={inList ? 'true' : 'false'}
                  className="flex-1 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white rounded-lg font-semibold text-sm shadow-md shadow-purple-900/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {inList
                    ? (lead.rationale?.includes('Overdue visit') ? 'Overdue — view visits' : 'Added to today')
                    : 'Add to today'}
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); onReject(lead.id); }}
                  disabled={busy}
                  data-testid="briefing-reject"
                  data-lead-id={lead.id}
                  className={`flex-shrink-0 px-4 py-2.5 rounded-lg font-semibold text-sm transition-colors hover:bg-gray-700 hover:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed ${S.muted}`}
                >
                  Not now
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div
        className={`px-4 py-4 border-t space-y-2 flex-shrink-0 bg-gray-800 ${S.rule}`}
        style={mobileSheet ? { paddingBottom: 'calc(1rem + var(--safe-bottom))' } : undefined}
      >
        {onAcceptAll && briefingData.some((lead) => !isLeadInVisitList(lead.global_id || `lead_${lead.id}`)) && (
          <button
            onClick={onAcceptAll}
            disabled={loadingLeadId !== null || isAcceptingAll}
            data-testid="briefing-accept-all"
            className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white rounded-lg font-semibold shadow-md shadow-purple-900/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isAcceptingAll
              ? 'Adding to visit list...'
              : `Add all (${briefingData.filter((lead) => !isLeadInVisitList(lead.global_id || `lead_${lead.id}`)).length} remaining)`
            }
          </button>
        )}
        <button
          onClick={onClose}
          data-testid="briefing-close-footer"
          className={`w-full px-6 py-3 rounded-lg font-semibold transition-colors hover:bg-gray-700 ${S.body}`}
        >
          Close briefing
        </button>
      </div>
    </div>
  );

  if (panelMode) return inner;

  if (mobileSheet) {
    return (
      <>
        <div className="fixed inset-0 bg-black/40 z-[54]" onClick={onClose} />
        <div
          ref={sheetRef}
          className="fixed inset-x-0 bottom-0 z-[55] rounded-t-2xl overflow-hidden flex flex-col"
          style={{ height: '88vh', transition: 'transform 0.3s ease-out' }}
        >
          <div className={`flex justify-center pt-3 pb-1 flex-shrink-0 ${S.shell}`}>
            <div className="w-10 h-1 rounded-full bg-gray-600" />
          </div>
          {inner}
        </div>
      </>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      {inner}
    </div>
  );
};

// Custom comparator: skip re-render unless props that affect the rendered output change.
// isLeadInVisitList and onAccept/onReject/onClose/onAcceptAll are function references —
// they will still cause a re-render when App.js recreates them, but we gate on the
// data-driven props so at minimum data-identical renders are skipped.
export default memo(AgentBriefing, (prev, next) => {
  return (
    prev.isOpen === next.isOpen &&
    JSON.stringify(prev.briefingData) === JSON.stringify(next.briefingData) &&
    prev.isLoading === next.isLoading &&
    prev.loadingLeadId === next.loadingLeadId &&
    prev.isAcceptingAll === next.isAcceptingAll &&
    prev.isLeadInVisitList === next.isLeadInVisitList &&
    prev.onClose === next.onClose &&
    prev.onAccept === next.onAccept &&
    prev.onAcceptAll === next.onAcceptAll &&
    prev.onReject === next.onReject &&
    prev.focusedLeadId === next.focusedLeadId &&
    prev.onLeadFocus === next.onLeadFocus &&
    prev.mobileSheet === next.mobileSheet
  );
});
